/**
 * Small presentational atoms shared across the Customer-360 tabs. Kept here (not in the base UI lib)
 * because they encode customer-domain semantics — utilization bands, a KPI tile, a labelled section —
 * not generic primitives. No cards-in-cards; sections are plain headed blocks.
 */

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Info, Layers } from "lucide-react";
import { cn } from "@/lib/cn";
import { formatPct } from "@/lib/format";
import { Badge, type StatusTone } from "@/components/ui";
import { asTKey } from "@/i18n/keys";

/**
 * Localized label for a server status token ("active", "PAID", …) — the copy twin of `statusTone`.
 *
 * Four call sites were each hand-rolling `t(\`status.${x}\`, { defaultValue: x })`, which typed the key as
 * `string` and so opted every one of them out of the key check. This is that pattern, once, checked: a
 * status with no copy returns null from `asTKey` and we render the RAW server token, exactly as the old
 * `defaultValue` did. Lower-cased before lookup because the invoices feed shouts its statuses ("PAID"),
 * while the keys in th.ts are lower-case — previously only InvoicesTab remembered to do this.
 */
export function statusLabel(t: TFunction, status: string): string {
  const key = asTKey("status.", status.toLowerCase());
  return key ? t(key) : status;
}

/** Map an account_status string → a StatusPill tone (label always carries meaning too). */
export function statusTone(status: string | null | undefined): StatusTone {
  switch ((status ?? "").toLowerCase()) {
    case "active":
    case "paid":
    case "collected":
    case "posted":
      return "ok";
    case "pending":
    case "trial":
    case "credit":
    case "sent":
      return "info";
    case "expiring":
    case "overdue":
    case "open":
      return "warn";
    case "expired":
    case "cancelled":
    case "suspended":
    case "void":
      return "danger";
    default:
      return "neutral";
  }
}

/**
 * The five canonical utilization bands. Derived-union (not a hand-written type) so every switch over a
 * band is exhaustive-checked and a new band cannot be added without the compiler flagging the gaps.
 */
export const UTIL_BANDS = ["high", "healthy", "low", "very_low", "unknown"] as const;
export type UtilBand = (typeof UTIL_BANDS)[number];

/**
 * ZPCD's own prose band ("High (Upsell)", "Very Low (Churn risk)", "Unknown", …) → a canonical band.
 * The parenthetical hint is stripped, so this also accepts the bare code. Anything unrecognised → the
 * no-data "unknown" band (never guesses a usage level).
 */
export function bandFromLabel(band: string | null | undefined): UtilBand {
  switch (
    (band ?? "")
      .toLowerCase()
      .replace(/\s*\([^)]*\)/, "")
      .trim()
  ) {
    case "high":
      return "high";
    case "healthy":
      return "healthy";
    case "low":
      return "low";
    case "very low":
      return "very_low";
    default:
      return "unknown";
  }
}

/**
 * Canonical band → tone. Business reading of a CSM screen: high licence usage = an UPSELL opportunity
 * (warn/amber — stands out so it gets acted on); low usage = CHURN risk (very_low → danger, low → info);
 * unknown = no usage data (neutral, NEVER a green "healthy" bar). Accepts a code OR ZPCD's prose band.
 */
export function utilizationTone(band: UtilBand | string | null | undefined): StatusTone {
  const code = (UTIL_BANDS as readonly string[]).includes(band ?? "")
    ? (band as UtilBand)
    : bandFromLabel(band);
  switch (code) {
    case "healthy":
      return "ok";
    case "high":
      return "warn";
    case "very_low":
      return "danger";
    case "low":
      return "info";
    case "unknown":
      return "neutral";
  }
}

/**
 * Coarse percentage → canonical band. SINGLE source of truth shared by the usage table, worklist bars
 * and the reports chart. Thresholds MATCH portal-api `_utilization_band` (90/50/30) so a bar computed
 * here and a band string computed server-side can never colour the same account differently. null → the
 * no-data "unknown" band.
 */
export function bandFromPct(pct: number | null | undefined): UtilBand {
  if (pct == null) return "unknown";
  if (pct >= 90) return "high";
  if (pct >= 50) return "healthy";
  if (pct >= 30) return "low";
  return "very_low";
}

/**
 * Localized short month name from a 1..12 month number (e.g. "มิ.ย." / "Jun"). Routes through i18n so a
 * Thai operator never sees an English month on a usage table or a printable report. Out-of-range months
 * fall back to the raw number rather than rendering blank.
 */
export function monthShort(t: TFunction, month: number): string {
  const key = asTKey("months.short.", month);
  return key ? t(key) : String(month);
}

/**
 * Localized short weekday name from an ISO weekday number (1=Monday..7=Sunday, matching Postgres
 * `extract(isodow from …)` — the Insights report's weekly-rhythm chart uses this directly, never a
 * hardcoded English "Mon"/"Tue"). Out-of-range values fall back to the raw number.
 */
export function weekdayShort(t: TFunction, isodow: number): string {
  const key = asTKey("weekdays.short.", isodow);
  return key ? t(key) : String(isodow);
}

/**
 * A KPI value tile. Label on top, big value, optional sub-line. No hero-metric template.
 *
 * `mono` (default true) renders the value in the tabular/mono numeric face for column alignment — keep
 * it true for numbers/dates/IDs. Set it FALSE when the value is non-numeric text (esp. Thai), because
 * the mono fallback fonts carry no Thai glyphs and would drop such text to a mismatched system face.
 */
export function KpiTile({
  label,
  value,
  sub,
  mono = true,
  tone = "default",
  className,
  title,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  mono?: boolean;
  /** "money" renders the value in the distinct teal-green money tone (a money surface, never status). */
  tone?: "default" | "money";
  className?: string;
  /** Hover tooltip on the whole tile (e.g. how the figure is counted). */
  title?: string;
}) {
  return (
    <div
      title={title}
      className={cn(
        "flex flex-col gap-1.5 rounded-(--radius) border border-(--border) bg-(--surface) p-4 shadow-(--shadow-sm)",
        className,
      )}
    >
      <span className="text-(length:--text-xs) font-medium text-(--text-muted)">{label}</span>
      <span
        className={cn(
          "text-(length:--text-xl) font-semibold leading-tight",
          tone === "money" ? "text-(--money)" : "text-(--text-strong)",
          mono && "tabular",
        )}
      >
        {value}
      </span>
      {sub && <span className="text-(length:--text-2xs) text-(--text-subtle)">{sub}</span>}
    </div>
  );
}

/** A plain section: a heading + optional trailing slot (e.g. a freshness badge) + the body below. */
export function Section({
  title,
  trailing,
  children,
  className,
  id,
}: {
  title: string;
  trailing?: ReactNode;
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section id={id} className={cn("flex flex-col gap-3", className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-(length:--text-base) font-semibold text-(--text-strong)">{title}</h2>
        {trailing}
      </div>
      {children}
    </section>
  );
}

/** A thin utilization meter (band-toned). Pure presentation; clamps 0–100, tolerates null. */
export function UtilizationBar({
  pct,
  tone = "ok",
  className,
}: {
  pct: number | null | undefined;
  tone?: StatusTone;
  className?: string;
}) {
  const clamped = pct == null ? 0 : Math.max(0, Math.min(100, pct));
  const barColor =
    tone === "danger"
      ? "bg-(--danger)"
      : tone === "warn"
        ? "bg-(--warn)"
        : tone === "info"
          ? "bg-(--info)"
          : tone === "neutral"
            ? "bg-(--text-subtle)"
            : "bg-(--ok)";
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-(--surface-sunken) ring-1 ring-inset ring-(--border-hairline)"
        role="meter"
        aria-valuenow={pct == null ? undefined : Math.round(clamped)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={cn(
            "h-full rounded-full transition-[width] duration-(--dur) ease-(--ease-out)",
            barColor,
          )}
          style={{ width: `${clamped}%` }}
        />
      </div>
      <span className="tabular w-12 shrink-0 text-right text-(length:--text-xs) text-(--text-muted)">
        {formatPct(pct)}
      </span>
    </div>
  );
}

/**
 * The account's plan-tier badge (bundle_tier_map). GRACEFUL: an unmapped tier (tier_known=false, or
 * tier "UNKNOWN"/empty) renders a neutral "Tier unknown" chip — it NEVER asserts a tier the mirror
 * can't vouch for (the *_bundled flags are unreliable for those 7,694 users). A known tier shows the
 * humanised tier name in the brand tone.
 */
export function TierBadge({ tier, tierKnown }: { tier: string | null; tierKnown: boolean }) {
  const { t } = useTranslation();
  const known = tierKnown && !!tier && tier.toUpperCase() !== "UNKNOWN";
  return (
    <Badge tone={known ? "brand" : "neutral"}>
      <Layers className="size-3" aria-hidden />
      {known ? t("customer.tier.label", { tier: humaniseTier(tier) }) : t("customer.tier.unknown")}
    </Badge>
  );
}

/** Title-case a tier token for display (e.g. "business_plus" → "Business Plus"). */
function humaniseTier(tier: string): string {
  return tier
    .split(/[._\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** A subtle informational line for a feed-frozen / data note. Never alarming; just informative. */
export function FeedNote({ children }: { children: ReactNode }) {
  return (
    <p className="flex items-start gap-2 rounded-(--radius-sm) bg-(--surface-sunken) px-3 py-2 text-(length:--text-2xs) leading-relaxed text-(--text-muted) ring-1 ring-inset ring-(--border-hairline)">
      <Info className="mt-px size-3.5 shrink-0 text-(--text-subtle)" aria-hidden />
      <span>{children}</span>
    </p>
  );
}
